'use client';

/**
 * The `name` field's editor — the deceased's four name parts as one row
 * (VALE-129, `components/attributes/CLAUDE.md` §3).
 *
 * **A popover rather than four rows, because a name parses back worse than an
 * address does.** `attribute-address-editor.tsx` is the model this is built
 * from directly: four separate rows (title · first · middle · last) read as
 * four unrelated fields, and reconstituting them from one flattened line would
 * mean parsing compound surnames, middle names and titles back apart — exactly
 * the loss the address popover exists to avoid for its own five parts.
 *
 * It keeps the same contract: the draft is live and untrimmed, `Escape`
 * reverts to the name the field opened with, and dismissing commits. `Tab`
 * moves between the four parts natively — the grid's field-to-field chaining
 * stops at this row deliberately, exactly as it does for `address`.
 */

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  ATTRIBUTE_EDITOR_BARE,
  ATTRIBUTE_INSET,
  ATTRIBUTE_LEADING,
  ATTRIBUTE_POPOVER_COLLISION_PADDING,
  ATTRIBUTE_POPOVER_PANEL,
  ATTRIBUTE_COMPOSITE_PART,
  ATTRIBUTE_COMPOSITE_PART_CONTROL,
  ATTRIBUTE_COMPOSITE_PART_LABEL,
  ATTRIBUTE_COMPOSITE_PARTS,
  ATTRIBUTE_VALUE_TEXT
} from './attribute-tokens';
import { attributePlaceholder } from './attribute-placeholder';
import type {
  AttributeDraft,
  AttributeNameParts,
  AttributeOption
} from './attribute-types';
import { useAttributePopoverCommit } from './use-attribute-popover-commit';

const NAME_TEXT_PARTS = [
  { key: 'firstName', label: 'First name' },
  { key: 'middleName', label: 'Middle name' },
  { key: 'lastName', label: 'Last name' }
] as const satisfies readonly {
  key: keyof AttributeNameParts;
  label: string;
}[];

/**
 * Assembles the four parts, skipping absent ones so a matter with no middle
 * name does not render a double space.
 *
 * A local, generic assembler rather than a reuse of `formatDecedentName`
 * (`lib/services/matters/utils.ts`): that helper reads `Partial<Matter>` and
 * falls back to `'Unnamed'`, both matters-specific — this package depends on
 * no feature (see this module's own header), and the trigger button needs the
 * shared `Set <label>…` empty state, not `Unnamed`.
 *
 * **Exported because the row's commit bridge renders it too** (VALE-1773). The
 * bridge draws the just-committed value in the row's read-out for the frames
 * between the editor closing and the host's record prop catching up, and it has
 * to draw it with *this* function rather than a second one: the reader is
 * looking at this trigger's output at the moment the editor closes, so anything
 * else would substitute one string for another in front of them. See
 * `attribute-draft-display.ts`.
 */
export function formatNameParts(name: AttributeNameParts | null): string {
  if (!name) return '';
  return [name.title, name.firstName, name.middleName, name.lastName]
    .filter((part) => (part ?? '').trim().length > 0)
    .join(' ');
}

/**
 * A name with every part blank is **cleared**, not stored as four empty
 * strings — the same rule `attribute-address-editor.tsx`'s `normalise` applies,
 * so an *"is anything captured here?"* read does not see an object and answer
 * yes.
 */
function normalise(name: AttributeNameParts): AttributeNameParts | null {
  const hasContent = [
    name.title,
    name.firstName,
    name.middleName,
    name.lastName
  ].some((part) => (part ?? '').trim().length > 0);
  return hasContent ? name : null;
}

export function AttributeNameEditor({
  label,
  name,
  titleOptions,
  onDraft,
  onRevert,
  onCommit
}: {
  label: string;
  name: AttributeNameParts | null;
  /**
   * The title select's options — spec-authored, so this stays feature-agnostic.
   *
   * **Omitted means the record has no title**, and the select is not rendered
   * (VALE-863) — the Contacts drawer's name is three parts, not four. An empty
   * array means the same thing: a select with nothing to choose is not a
   * narrower version of the control, it is a broken one.
   */
  titleOptions?: AttributeOption[];
  onDraft: (next: AttributeDraft) => void;
  onRevert: () => void;
  onCommit: (value: AttributeDraft) => void;
}) {
  const hasTitle = (titleOptions?.length ?? 0) > 0;

  const set = (key: keyof AttributeNameParts, value: string) => {
    // Untrimmed, per §4.3 — trimming here strips a space the moment it is
    // typed, same as every other field in this treatment.
    onDraft(normalise({ ...(name ?? {}), [key]: value }));
  };

  /*
    `Enter` commits and closes, `Escape` reverts, dismissing commits: the one
    contract all seven composite popover editors share (VALE-1321). See
    `./use-attribute-popover-commit`, which owns every subtlety in it.
  */
  const popoverCommit = useAttributePopoverCommit({
    draft: name,
    onRevert,
    onCommit
  });

  return (
    <Popover {...popoverCommit.popover}>
      <PopoverTrigger asChild>
        {/*
          Left-aligned and cell-width, matching the read-out it replaces — the
          trigger sits in the same slot, so the name must not jump sideways as
          the popover opens. Bare: the cell draws the `slate-50` fill and the
          ring (see `AttributeFieldControl`'s `unified` arm).
        */}
        <button
          type="button"
          aria-label={label}
          className={cn(
            ATTRIBUTE_VALUE_TEXT,
            ATTRIBUTE_INSET,
            ATTRIBUTE_LEADING,
            'text-foreground min-w-0 flex-1 truncate rounded-md text-left',
            ATTRIBUTE_EDITOR_BARE
          )}
        >
          {formatNameParts(name) || attributePlaceholder(label)}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={ATTRIBUTE_POPOVER_COLLISION_PADDING}
        className={cn(ATTRIBUTE_POPOVER_PANEL, ATTRIBUTE_COMPOSITE_PARTS)}
        {...popoverCommit.content}
      >
        {/*
          **No options means the person has no title part, and the control is
          omitted rather than rendered empty.** A `name` row is not always a
          deceased: the enquiry drawer's Enquirer and Partner rows compose three
          parts and store no honorific at all, and an always-rendered `Select`
          gave them a picker whose list was empty — an affordance that can only
          disappoint. Keyed on the options rather than on a second flag, because
          "what can this title be" and "does this name have one" are the same
          question asked once.

          `hasTitle` is that same predicate, kept because the parts below read
          it too (VALE-863).
        */}
        {hasTitle && (
          <div className={ATTRIBUTE_COMPOSITE_PART}>
            <Label
              htmlFor="attribute-name-title"
              className={ATTRIBUTE_COMPOSITE_PART_LABEL}
            >
              Title
            </Label>
            <Select
              value={name?.title ?? ''}
              onValueChange={(next) => set('title', next)}
            >
              <SelectTrigger
                id="attribute-name-title"
                aria-label="Title"
                className={ATTRIBUTE_COMPOSITE_PART_CONTROL}
              >
                <SelectValue placeholder="Title" />
              </SelectTrigger>
              <SelectContent>
                {(titleOptions ?? []).map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    className={ATTRIBUTE_VALUE_TEXT}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {NAME_TEXT_PARTS.map(({ key, label: partLabel }, index) => (
          <div key={key} className={ATTRIBUTE_COMPOSITE_PART}>
            <Label
              htmlFor={`attribute-name-${key}`}
              className={ATTRIBUTE_COMPOSITE_PART_LABEL}
            >
              {partLabel}
            </Label>
            <Input
              id={`attribute-name-${key}`}
              aria-label={partLabel}
              /*
                `First name` claims focus in both shapes — a Radix `Select`
                trigger does not take it, so the Title part above was never
                focused either. Gating this on `hasTitle` (VALE-863's first
                attempt) would have left the matter's name editor opening with
                focus nowhere.
              */
              autoFocus={index === 0}
              value={name?.[key] ?? ''}
              onChange={(event) => set(key, event.target.value)}
              className={ATTRIBUTE_COMPOSITE_PART_CONTROL}
            />
          </div>
        ))}
        {popoverCommit.confirm}
      </PopoverContent>
    </Popover>
  );
}
